import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useWeb3React } from '@web3-react/core'
import { WalletConnect } from '@web3-react/walletconnect'
import { chainIDs } from 'config'
import { useRouter } from 'next/router'
import { signOut } from 'next-auth/react'
import { getChainName } from 'utils/web3/chains'

import PageLoader from 'components/Loaders/PageLoader'
import { toastify } from 'components/Toast'

interface IPlatformContext {
  disconnectWallet: () => void
  platformChainId: number
  changePlatformChain: (
    _chain: number | string | null,
    _ignoreToast?: boolean
  ) => boolean
}

const defaultState = {
  disconnectWallet: () => null,
  platformChainId: 137,
  changePlatformChain: () => true,
}

const PlatformContext = createContext<IPlatformContext>(defaultState)

interface IPlatformProvider {
  children: ReactNode
}

export const PlatformProvider: FC<IPlatformProvider> = ({ children }) => {
  const router = useRouter()
  const { network } = router.query
  const { account, connector } = useWeb3React()
  const [platformChainId, setPlatformChainId] = useState<number>(137)

  const changePlatformChain = (
    _chainId: number | string | null,
    ignoreToast = false
  ) => {
    if (
      _chainId &&
      !isNaN(Number(_chainId)) &&
      chainIDs.includes(Number(_chainId))
    ) {
      setPlatformChainId(Number(_chainId))
      // Chain Id supported
      if (!ignoreToast) {
        toastify.dismiss()
        toastify.success({
          title: 'Platform network changed',
          description: `Network changed to ${getChainName(Number(_chainId))}`,
        })
      }
      return true
    } else {
      // Chain Id not supported
      if (!ignoreToast) {
        toastify.dismiss()
        toastify.error({
          title: 'Platform network not supported',
          description: `Network ID ${_chainId} is not currently supported`,
        })
      }
      return false
    }
  }

  useEffect(() => {
    /// @dev Commenting for testing on matic only
    if (window && window.localStorage) {
      const _chainId = window.localStorage.getItem('chainId')
      changePlatformChain(_chainId, true)
    }
  }, [])

  useEffect(() => {
    if (!!network && chainIDs.includes(Number(network))) {
      changePlatformChain(Number(network), true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network])

  useEffect(() => {
    if (window && window.localStorage) {
      window.localStorage.setItem('chainId', platformChainId.toString())
    }
  }, [platformChainId])

  useEffect(() => {
    if (!!account) {
      if (window && window.localStorage) {
        console.log('[useEffect] User changed the wallet')
        window.localStorage.setItem('simple_user', account.toLowerCase())
        window.localStorage.setItem(
          'connector',
          connector instanceof WalletConnect ? 'WalletConnect' : 'MetaMask'
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account])

  const disconnectWallet = () => {
    if (connector?.deactivate) {
      void connector.deactivate()
    } else {
      void connector.resetState()
    }
    console.log(window.localStorage.removeItem('simple_user'))
    console.log(window.localStorage.removeItem('connector'))
    signOut({ redirect: false })
  }

  if (platformChainId === 0) return <PageLoader />
  return (
    <PlatformContext.Provider
      value={{
        disconnectWallet,
        platformChainId,
        changePlatformChain,
      }}
    >
      {children}
    </PlatformContext.Provider>
  )
}

export const usePlatformContext = () =>
  useContext(PlatformContext) as IPlatformContext

export default PlatformContext
